import {view, assignTokens, Icon, tokens} from '@sail/ui';
import {thumbsUp, thumbsDown} from '@sail/icons/react/Icon';
import {defineMessages, useIntl} from 'react-intl';
import {useState} from 'react';
import {useAnalytics} from '../../AnalyticsProvider';
import {useTheme} from '../../ThemeProvider';
import {dotComColors, dotComTransitions} from '../../theme';

const answerFeedback = 'universal_help_live_answers_frontend_feedback';

const messages = defineMessages({
  thumbsUpAriaLabel: {
    id: 'conversations_ui.mse_inline_data.feedback.thumbs_up.ariaLabel',
    description: 'Label for the LLM feedback thumbs up',
    defaultMessage: 'Good response',
  },
  thumbsDownAriaLabel: {
    id: 'conversations_ui.mse_inline_data.feedback.thumbs_down.ariaLabel',
    description: 'Label for the LLM feedback thumbs down',
    defaultMessage: 'Bad response',
  },
});

type FeedbackButtonProps = {
  icon: typeof thumbsUp | typeof thumbsDown;
  ariaLabel: string;
  isSelected: boolean;
  onClick: () => void;
};

const FeedbackButton = ({
  icon,
  ariaLabel,
  isSelected,
  onClick,
}: FeedbackButtonProps) => {
  const theme = useTheme();

  return (
    <view.button
      aria-label={ariaLabel}
      css={{
        height: 24,
        width: 24,
        stack: 'x',
        alignX: 'center',
        alignY: 'center',
        border: '1px solid',
        borderColor: isSelected ? theme.linkColor : dotComColors.brand300,
        background: isSelected ? theme.linkColor : dotComColors.brand,
        borderRadius: 'medium',
        transition: dotComTransitions.buttonTransition,
        color: isSelected ? dotComColors.brand : dotComColors.brand600,
        ':hover': isSelected
          ? {}
          : {
              color: theme.linkColor,
              borderColor: '#c4ccd8',
              boxShadow:
                '0 0.25rem 0.75rem 0 rgba(0, 0, 0, 0.06), 0 0.125rem 0.25rem 0 rgba(0, 0, 0, 0.1)',
            },
        ':focus-visible': {
          outline: theme.focusVisibleOutline as unknown as string,
        },
      }}
      onClick={isSelected ? undefined : onClick}
    >
      <Icon icon={icon} size="xsmall" css={{fill: 'currentColor'}} />
    </view.button>
  );
};

export type FeedbackButtonsProps = {
  streamingConversationId?: string;
};

export const FeedbackButtons = (props: FeedbackButtonsProps) => {
  const {streamingConversationId} = props;

  const [selectedFeedback, setSelectedFeedback] = useState<
    'thumbs_up' | 'thumbs_down' | undefined
  >();
  const analytics = useAnalytics();
  const intl = useIntl();

  return (
    <view.div
      uses={[
        assignTokens(tokens.color.icon.action.primary, dotComColors.brand600),
      ]}
      css={{gap: 'xsmall', stack: 'x'}}
    >
      <FeedbackButton
        icon={thumbsUp}
        ariaLabel={intl.formatMessage(messages.thumbsUpAriaLabel)}
        isSelected={selectedFeedback === 'thumbs_up'}
        onClick={() => {
          setSelectedFeedback('thumbs_up');
          analytics.action(answerFeedback, {
            feedback: 'thumbs_up',
            streaming_conversation_id: streamingConversationId,
          });
        }}
      />
      <FeedbackButton
        icon={thumbsDown}
        ariaLabel={intl.formatMessage(messages.thumbsDownAriaLabel)}
        isSelected={selectedFeedback === 'thumbs_down'}
        onClick={() => {
          setSelectedFeedback('thumbs_down');
          analytics.action(answerFeedback, {
            feedback: 'thumbs_down',
            streaming_conversation_id: streamingConversationId,
          });
        }}
      />
    </view.div>
  );
};
